import { useLocation } from 'react-router'
import { Link } from 'react-router-dom'

import { Space } from '@tribeplatform/gql-client/types'
import {
  SystemSpaceSlug,
  useAuthMember,
  useSpacesBySlugs,
  useSpaceUtil,
} from '@tribeplatform/react-sdk/hooks'
import { NavVertical } from '@tribeplatform/react-ui-kit/Sidebar'

import { useMobileSidebar } from '../../MobileSidebarProvider'
import {
  MainMenuAction,
  mainMenuActiveIcons,
  mainMenuIcons,
  MainMenuItem,
} from './constants'
import { MainMenuSettings } from './settings/MainMenuSettings'
import { cleanDeprecateItems } from './utils'

export type MainMenuProps = {
  items?: MainMenuItem[]
}

export const MainMenu = ({ items: notCleanedItems = [] }: MainMenuProps) => {
  const items = cleanDeprecateItems(notCleanedItems)
  const spaceSlugs = items
    .filter(item => item.action === MainMenuAction.OPEN_SPACE)
    .map(item => item.spaceSlug)
  const { data: spaces = [] } = useSpacesBySlugs({
    fields: {
      image: 'basic',
      banner: 'basic',
      authMemberProps: 'all',
      slate: 'all',
    },
    variables: { slugs: spaceSlugs },
  })
  const { setMobileSidebarOpen } = useMobileSidebar()
  const { data: authMember } = useAuthMember()
  const location = useLocation()
  const { generatePath, generateProfilePath, matchPath, hasViewPermission } =
    useSpaceUtil()

  return (
    <NavVertical className="block-main-menu">
      <NavVertical.Group>
        {items
          .map(item => {
            if (!item.enabled) return null
            const IconComponent = mainMenuIcons[item.icon]
            const ActiveIconComponent = mainMenuActiveIcons[item.icon]
            let space: Space
            let matched
            let isActive: boolean

            switch (item.action) {
              case MainMenuAction.OPEN_LINK:
                return (
                  <NavVertical.Item
                    key={item.label}
                    as={Link}
                    to={item.link}
                    active={false}
                    leadingIcon={<IconComponent />}
                    target="_blank"
                    onClick={() => setMobileSidebarOpen(false)}
                  >
                    {item.label}
                  </NavVertical.Item>
                )
              case MainMenuAction.OPEN_PROFILE:
                if (!hasViewPermission(SystemSpaceSlug.MEMBER)) return null
                matched = matchPath(SystemSpaceSlug.MEMBER, location.pathname)
                isActive = matched && matched?.params?.id === authMember?.id
                return (
                  <NavVertical.Item
                    key={item.label}
                    as={Link}
                    to={generateProfilePath()}
                    active={isActive}
                    leadingIcon={
                      isActive ? <ActiveIconComponent /> : <IconComponent />
                    }
                    onClick={() => setMobileSidebarOpen(false)}
                  >
                    {item.label}
                  </NavVertical.Item>
                )
              case MainMenuAction.OPEN_SPACE:
                space = spaces?.find(space => space.slug === item.spaceSlug)
                if (!space || !hasViewPermission(space?.slug)) {
                  return null
                }
                matched = matchPath(space, location.pathname)
                return (
                  <NavVertical.Item
                    key={item.label}
                    as={Link}
                    to={generatePath(space)}
                    active={!!matched}
                    leadingIcon={
                      matched ? <ActiveIconComponent /> : <IconComponent />
                    }
                    onClick={() => setMobileSidebarOpen(false)}
                  >
                    {item.label}
                  </NavVertical.Item>
                )
              default:
                return null
            }
          })
          .filter(item => item)}
      </NavVertical.Group>
    </NavVertical>
  )
}

MainMenu.displayName = 'Main Menu'
MainMenu.Settings = MainMenuSettings
